import type { ActionFunctionArgs, LoaderFunctionArgs } from "@remix-run/node";
import { json, redirect } from "@remix-run/node";
import { useLoaderData, useRouteLoaderData } from "@remix-run/react";

import { AppErrorBoundary } from "~/components/AppErrorBoundary";
import { createServerStateManager } from "~/modules/StateManager.server";
import { Timetable } from "~/modules/timetable/components/Timetable";
import {
  TimetableContextProvider,
  useTimetableRouteContext,
} from "~/modules/timetable/components/Timetable.hooks";
import { handleTimetableAction } from "~/modules/timetable/timetable.action.server";
import { handleTimetableLoader } from "~/modules/timetable/timetable.loader.server";
import { requireStep } from "~/utils/validators.utils.server";

export async function action(args: ActionFunctionArgs) {
  return args.context.measurer.time(
    "$enterprise_invite_timetable_action",
    async function scheduleTimetableLoader() {
      const stateManager = await createServerStateManager(args);
      const { nextStep } = stateManager.getStepConfig("timetable");
      requireStep(nextStep, "Timetable");

      await handleTimetableAction(stateManager);

      return redirect(`../${nextStep.pathname}`);
    },
  );
}

export function useInviteTimetableLoaderData() {
  return useRouteLoaderData<typeof loader>(
    "routes/$enterprise+/plugin+/$sessionId.appointment.invite.timetable",
  );
}

export async function loader(args: LoaderFunctionArgs) {
  return args.context.measurer.time(
    "$enterprise/invite/timetable",
    async function inviteTimetableLoader() {
      const stateManager = await createServerStateManager(args);
      const { nextStep } = stateManager.getStepConfig("timetable");
      requireStep(nextStep, "Timetable");

      const result = await handleTimetableLoader(stateManager);

      if (result.type === "redirect") {
        return redirect(`../${nextStep.pathname}`);
      }

      return json(result.payload);
    },
  );
}

export default function TimetableRoute() {
  const props = useLoaderData<typeof loader>();
  const context = useTimetableRouteContext();

  return (
    <div className="flex flex-col gap-sm">
      <TimetableContextProvider value={context}>
        <Timetable {...props} />
      </TimetableContextProvider>
    </div>
  );
}

export function ErrorBoundary() {
  return <AppErrorBoundary />;
}
